import type { ThemeName } from "../semantic/types";

function getNodeEnv(): string | undefined {
  const g = globalThis as unknown as {
    process?: { env?: { NODE_ENV?: string } };
  };
  return g.process?.env?.NODE_ENV;
}

function isProductionEnv(): boolean {
  return getNodeEnv() === "production";
}

type MinimalSessionStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

/** Sync key/value store (e.g. `sessionStorage` on web, MMKV shim on React Native). */
export type ThemeDebugSessionStorage = MinimalSessionStorage;

const THEME_DEBUG_GLOBAL_STORAGE_KEY = "__oaThemeDebugSessionStorage";
const THEME_DEBUG_NATIVE_EMITTER_KEY = "__oaThemeDebugNativeEmitter";

/**
 * Install sync storage on React Native **before** `ThemeProvider` mounts (e.g. MMKV).
 * Required for theme debug persistence and for `ThemeInitializer` override detection.
 */
export function installThemeDebugNativeBridge(
  storage: ThemeDebugSessionStorage,
): void {
  Reflect.set(globalThis, THEME_DEBUG_GLOBAL_STORAGE_KEY, storage);
}

export type ThemeDebugEventDetail = {
  theme?: ThemeName;
  cleared?: boolean;
};

type NativeThemeDebugEmitter = {
  subscribe: (fn: (d: ThemeDebugEventDetail) => void) => () => void;
  emit: (d: ThemeDebugEventDetail) => void;
};

function getOrCreateNativeEmitter(): NativeThemeDebugEmitter {
  let emitter = Reflect.get(globalThis, THEME_DEBUG_NATIVE_EMITTER_KEY) as
    | NativeThemeDebugEmitter
    | undefined;
  if (!emitter) {
    const listeners = new Set<(d: ThemeDebugEventDetail) => void>();
    emitter = {
      subscribe(fn) {
        listeners.add(fn);
        return () => listeners.delete(fn);
      },
      emit(d) {
        listeners.forEach((fn) => {
          fn(d);
        });
      },
    };
    Reflect.set(globalThis, THEME_DEBUG_NATIVE_EMITTER_KEY, emitter);
  }
  return emitter;
}

/** Safe when TS is compiled without DOM lib (e.g. React Native packages importing tokens). */
function getSessionStorage(): MinimalSessionStorage | undefined {
  try {
    const installed = Reflect.get(
      globalThis,
      THEME_DEBUG_GLOBAL_STORAGE_KEY,
    ) as MinimalSessionStorage | undefined;
    if (
      installed &&
      typeof installed === "object" &&
      typeof installed.getItem === "function"
    ) {
      return installed;
    }
  } catch {
    /* empty */
  }
  try {
    const ss = Reflect.get(globalThis, "sessionStorage");
    if (
      ss &&
      typeof ss === "object" &&
      typeof (ss as { getItem?: unknown }).getItem === "function"
    ) {
      return ss as MinimalSessionStorage;
    }
  } catch {
    /* private mode / non-browser */
  }
  return undefined;
}

function getWindow():
  | {
      dispatchEvent(event: unknown): boolean;
      addEventListener(type: string, listener: (ev: unknown) => void): void;
      removeEventListener(type: string, listener: (ev: unknown) => void): void;
    }
  | undefined {
  try {
    const w = Reflect.get(globalThis, "window");
    if (w && typeof w === "object" && "addEventListener" in w) {
      return w as {
        dispatchEvent(event: unknown): boolean;
        addEventListener(type: string, listener: (ev: unknown) => void): void;
        removeEventListener(
          type: string,
          listener: (ev: unknown) => void,
        ): void;
      };
    }
  } catch {
    /* empty */
  }
  return undefined;
}

/** Dispatched on `window` when dev theme debug changes (all ThemeProvider instances listen). */
export const THEME_DEBUG_EVENT = "oneapp-theme-debug" as const;

export const THEME_DEBUG_STORAGE_KEY = "oa-theme-debug";
export const THEME_DEBUG_ACTIVE_KEY = "oa-theme-debug-active";

const THEME_NAMES = ["learn", "meta", "sriChaitanya"] as const;

function dispatchThemeDebugCustomEvent(detail: ThemeDebugEventDetail): void {
  const win = getWindow();
  if (win?.dispatchEvent) {
    const CustomEventCtor = Reflect.get(globalThis, "CustomEvent") as
      | (new <D>(type: string, eventInitDict?: { detail?: D }) => unknown)
      | undefined;
    if (CustomEventCtor) {
      const ev = new CustomEventCtor(THEME_DEBUG_EVENT, { detail });
      win.dispatchEvent(ev);
    }
    return;
  }
  getOrCreateNativeEmitter().emit(detail);
}

export function isThemeDebugSyncEnabled(): boolean {
  return !isProductionEnv();
}

export function isValidThemeName(
  value: string | null | undefined,
): value is ThemeName {
  return (
    typeof value === "string" &&
    (THEME_NAMES as readonly string[]).includes(value)
  );
}

export function readThemeDebugFromSession(): ThemeName | null {
  if (isProductionEnv()) {
    return null;
  }
  const sessionStorage = getSessionStorage();
  if (!sessionStorage) {
    return null;
  }
  try {
    if (sessionStorage.getItem(THEME_DEBUG_ACTIVE_KEY) !== "1") {
      return null;
    }
    const raw = sessionStorage.getItem(THEME_DEBUG_STORAGE_KEY);
    return isValidThemeName(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function isThemeDebugOverrideActive(): boolean {
  return readThemeDebugFromSession() !== null;
}

/** Persist and broadcast so every ThemeProvider (including nested) stays in sync. */
export function applyThemeDebug(theme: ThemeName): void {
  if (isProductionEnv()) {
    return;
  }
  const sessionStorage = getSessionStorage();
  if (sessionStorage) {
    try {
      sessionStorage.setItem(THEME_DEBUG_STORAGE_KEY, theme);
      sessionStorage.setItem(THEME_DEBUG_ACTIVE_KEY, "1");
    } catch {
      /* ignore quota / private mode */
    }
  }
  dispatchThemeDebugCustomEvent({ theme });
}

export function clearThemeDebug(): void {
  if (isProductionEnv()) {
    return;
  }
  const sessionStorage = getSessionStorage();
  if (sessionStorage) {
    try {
      sessionStorage.removeItem(THEME_DEBUG_STORAGE_KEY);
      sessionStorage.removeItem(THEME_DEBUG_ACTIVE_KEY);
    } catch {
      /* ignore */
    }
  }
  dispatchThemeDebugCustomEvent({ cleared: true });
}

/**
 * Subscribe to theme debug events without referencing DOM globals (for ThemeProvider when
 * compiled without DOM lib, e.g. React Native).
 */
export function subscribeToThemeDebugEvents(
  handler: (detail: ThemeDebugEventDetail) => void,
): () => void {
  const win = getWindow();
  if (win?.addEventListener) {
    const listener = (ev: unknown) => {
      const detail = (ev as { detail?: ThemeDebugEventDetail })?.detail;
      if (detail) {
        handler(detail);
      }
    };
    win.addEventListener(THEME_DEBUG_EVENT, listener);
    return () => win.removeEventListener(THEME_DEBUG_EVENT, listener);
  }
  return getOrCreateNativeEmitter().subscribe(handler);
}
